import {
  AppBar,
  Box,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  LinearProgress,
  ListItemIcon,
  Menu,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Button
} from "@material-ui/core";
import Snackbar from "@material-ui/core/Snackbar";
import CloseIcon from "@material-ui/icons/Close";
import DehazeIcon from "@material-ui/icons/Dehaze";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import SettingsIcon from "@material-ui/icons/Settings";
import FilterListIcon from "@material-ui/icons/FilterList";
import ArrowRightIcon from "@material-ui/icons/ArrowRight";
import { DataExportMenuItem } from "data/DataExportMenuItem";
import { docsService } from "docs/DocsService";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation
} from "react-router-dom";
import { WebSocketStatusPanel } from "websocket/WebSocketStatusPanel";
import { AdministrationPage } from "../administration/AdministrationPage";
import { AUTH_SIGNOUT } from "../auth/AuthorizationTypes";
import { useEmail } from "../auth/useEmail";
import { DashboardsRoutePage } from "../dashboards/DashboardsRoutePage";
import { SET_ENGINEERS_DATE_FILTER, FETCH_ENGINEERS } from "../engineers/EngineersTypes";
import { SET_EMS_DATE_FILTER, FETCH_EMS } from "../ems/EMsTypes";
import { SET_CUSTOMERS_DATE_FILTER, FETCH_CUSTOMERS } from "../customers/CustomerTypes";
import { SET_TEAMS_DATE_FILTER, FETCH_TEAMS } from "../teams/TeamsTypes";
import {
  AppState,
  REMOVE_APP_FEEDBACK,
  ReportFeedbackAction,
  REPORT_APP_FEEDBACK,
  SET_APP_DATE_FILTER
} from "../redux/AppTypes";
import fetchService from "../utils/FetchService";
import { useSynching } from "../utils/useSynching";
import { dateFilterStorage, isValidDateFilter } from "../utils/dateFilterStorage";
import "./App.css";

const DEFAULT_TAB = "dashboards";

export function AuthorizedApp() {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const email = useEmail();
  const [tab, setTab] = useState(DEFAULT_TAB);
  const [moreOptionsMenuEl, setMoreOptionsMenuEl] = React.useState(null as any);
  const [systemSettingsMenuEl, setSystemSettingsMenuEl] = React.useState(null as any);
  const [dateFilterDialogOpen, setDateFilterDialogOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState('');
  const synching = useSynching();
  const feedback = useSelector((state: AppState) => state.feedback);
  const currentDateFilter = useSelector(
    (state: AppState) => state.dateFilter || ''
  );

  useEffect(() => {
    fetchService.subscribe({
      onError: (message) => {
        const action: ReportFeedbackAction = {
          type: REPORT_APP_FEEDBACK,
          feedback: message,
        };
        dispatch(action);
      },
    });
  }, [dispatch]);

  useEffect(() => {
    const l = location.pathname.split("/");
    const tab = l.length > 1 && l[1].length > 0 ? l[1] : DEFAULT_TAB;
    setTab(tab);
  }, [location]);

  const removeFeedback = () => {
    dispatch({ type: REMOVE_APP_FEEDBACK });
  };

  const handleSystemSettingsClick = (event: React.MouseEvent<HTMLElement>) => {
    setSystemSettingsMenuEl(event.currentTarget);
  };

  const handleSystemSettingsClose = () => {
    setSystemSettingsMenuEl(null);
  };

  const handleDataFilterClick = () => {
    setSystemSettingsMenuEl(null);
    setMoreOptionsMenuEl(null);
    // Pre-populate the date picker with the current filter
    setSelectedDate(currentDateFilter);
    setDateFilterDialogOpen(true);
  };

  const handleDateFilterClose = () => {
    setDateFilterDialogOpen(false);
  };

  const handleDateChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(event.target.value);
  };

  const handleDateFilterApply = () => {
    if (!isValidDateFilter(selectedDate)) {
      const action: ReportFeedbackAction = {
        type: REPORT_APP_FEEDBACK,
        feedback: "Invalid date filter format. Use YYYY-MM-DD.",
      };
      dispatch(action);
      return;
    }

    // Set the date filter for all entities and refetch them
    dispatch({ type: SET_APP_DATE_FILTER, dateFilter: selectedDate });
    dispatch({ type: SET_ENGINEERS_DATE_FILTER, dateFilter: selectedDate });
    dispatch({ type: SET_EMS_DATE_FILTER, dateFilter: selectedDate });
    dispatch({ type: SET_CUSTOMERS_DATE_FILTER, dateFilter: selectedDate });
    dispatch({ type: SET_TEAMS_DATE_FILTER, dateFilter: selectedDate });

    // Refetch all entities with the new filter
    dispatch({ type: FETCH_ENGINEERS });
    dispatch({ type: FETCH_EMS });
    dispatch({ type: FETCH_CUSTOMERS });
    dispatch({ type: FETCH_TEAMS });
    setDateFilterDialogOpen(false);
  };

  const handleClearFilter = () => {
    // Clear from localStorage
    dateFilterStorage.remove();

    // Calculate default date (1 year ago)
    const oneYearAgo = new Date();
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    const defaultDate = oneYearAgo.toISOString().split('T')[0];

    // Set the default date filter for all entities
    dispatch({ type: SET_APP_DATE_FILTER, dateFilter: defaultDate });
    dispatch({ type: SET_ENGINEERS_DATE_FILTER, dateFilter: defaultDate });
    dispatch({ type: SET_EMS_DATE_FILTER, dateFilter: defaultDate });
    dispatch({ type: SET_CUSTOMERS_DATE_FILTER, dateFilter: defaultDate });
    dispatch({ type: SET_TEAMS_DATE_FILTER, dateFilter: defaultDate });

    // Refetch all entities with the default filter
    dispatch({ type: FETCH_ENGINEERS });
    dispatch({ type: FETCH_EMS });
    dispatch({ type: FETCH_CUSTOMERS });
    dispatch({ type: FETCH_TEAMS });

    setDateFilterDialogOpen(false);
  };

  return (
    <Box style={{ marginBottom: 32 }}>
      <AppBar position="fixed">
        <Grid justifyContent="space-between" alignItems="center" container>
          <Grid item>
            <Tabs
              id="main-navigation"
              onChange={(e, value) => history.push(`/${value}`)}
              value={tab}
            >
              <Tab label="Dashboards" value="dashboards" />
              <Tab label="Administration" value="administration" />
              <Tab
                label="Help"
                value="help"
                onClick={() => docsService.getDocs("/index.html")}
              />
            </Tabs>
          </Grid>
          <Grid item>
            <IconButton
              aria-label="more"
              onClick={(e) => setMoreOptionsMenuEl(e.target)}
            >
              <DehazeIcon style={{ color: "white" }} />
            </IconButton>
            <Menu
              anchorEl={moreOptionsMenuEl}
              keepMounted
              open={Boolean(moreOptionsMenuEl)}
              onClose={() => setMoreOptionsMenuEl(null)}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              transformOrigin={{ vertical: "top", horizontal: "center" }}
            >
              <DataExportMenuItem />
              <MenuItem onClick={handleSystemSettingsClick}>
                <ListItemIcon>
                  <SettingsIcon />
                </ListItemIcon>
                System Settings
                <ArrowRightIcon style={{ marginLeft: 'auto' }} />
              </MenuItem>
              <MenuItem onClick={() => dispatch({ type: AUTH_SIGNOUT })}>
                <ListItemIcon>
                  <ExitToAppIcon />
                </ListItemIcon>
                Logout ({email})
              </MenuItem>
            </Menu>
            <Menu
              anchorEl={systemSettingsMenuEl}
              keepMounted
              open={Boolean(systemSettingsMenuEl)}
              onClose={handleSystemSettingsClose}
              getContentAnchorEl={null}
              anchorOrigin={{ vertical: "top", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              <MenuItem onClick={handleDataFilterClick}>
                <ListItemIcon>
                  <FilterListIcon />
                </ListItemIcon>
                Data Filter {currentDateFilter && `(${currentDateFilter})`}
              </MenuItem>
            </Menu>
          </Grid>
        </Grid>
        <LinearProgress
          style={{
            visibility: synching.any ? "visible" : "hidden",
          }}
        />
      </AppBar>
      <Box style={{ marginTop: 64 }}>
        <WebSocketStatusPanel />
        <Switch>
          <Route path="/dashboards">
            <Container maxWidth="xl">
              <DashboardsRoutePage />
            </Container>
          </Route>
          <Route path="/administration">
            <AdministrationPage />
          </Route>
          <Route path="/docs">Not found</Route>
          <Route path="/">
            <Redirect to={`/${DEFAULT_TAB}`} />
          </Route>
        </Switch>
      </Box>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        open={feedback.length > 0}
        autoHideDuration={6000}
        message={feedback[0]}
        onClose={removeFeedback}
        action={
          <React.Fragment>
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={removeFeedback}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </React.Fragment>
        }
      />
      <Dialog
        open={dateFilterDialogOpen}
        onClose={handleDateFilterClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Data Filter</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Select Date"
            type="date"
            fullWidth
            variant="outlined"
            value={selectedDate}
            onChange={handleDateChange}
            InputLabelProps={{
              shrink: true,
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearFilter} color="secondary" variant="contained">
            Reset to Default (1 year)
          </Button>
          <Box style={{ flex: 1 }} />
          <Button onClick={handleDateFilterClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDateFilterApply} color="primary" variant="contained">
            Apply Filter
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
